import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

/**
 * ResponsivePickers
 *
 * Seleção de início e término por DIA e MÊS apenas.
 * O ANO é sempre o ano letivo da escola (prop `schoolYear`) — o usuário não escolhe.
 *
 * Props:
 *  - schoolYear .............. ano letivo da escola (number | string). OBRIGATÓRIO.
 *  - selectedDateStart, selectedDateEnd .... valor inicial (Date | ISO | ''), opcional
 *  - setSelectedDateStart, setSelectedDateEnd
 *  - setDayStart, setMonthStart, setYearStart
 *  - setDayEnd, setMonthEnd, setYearEnd
 */

const MONTHS = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
];

// Quantidade de dias no mês/ano
const daysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(Number(year), Number(month), 0).getDate();
};

const ResponsivePickers = ({
    schoolYear,
    selectedDateStart,
    selectedDateEnd,
    setSelectedDateStart,
    setDayStart,
    setMonthStart,
    setYearStart,
    setSelectedDateEnd,
    setDayEnd,
    setMonthEnd,
    setYearEnd,
}) => {
    const [startDay, setStartDayLocal] = useState('');
    const [startMonth, setStartMonthLocal] = useState('');
    const [endDay, setEndDayLocal] = useState('');
    const [endMonth, setEndMonthLocal] = useState('');

    // Ano letivo sempre reportado ao pai
    useEffect(() => {
        if (schoolYear) {
            setYearStart && setYearStart(Number(schoolYear));
            setYearEnd && setYearEnd(Number(schoolYear));
        }
    }, [schoolYear, setYearStart, setYearEnd]);

    // Pré-preenche dia/mês a partir de uma data salva (edição)
    useEffect(() => {
        if (!selectedDateStart) return;
        const d = new Date(selectedDateStart);
        if (isNaN(d.getTime())) return;
        setStartDayLocal(d.getUTCDate());
        setStartMonthLocal(d.getUTCMonth() + 1);
    }, [selectedDateStart]);

    useEffect(() => {
        if (!selectedDateEnd) return;
        const d = new Date(selectedDateEnd);
        if (isNaN(d.getTime())) return;
        setEndDayLocal(d.getUTCDate());
        setEndMonthLocal(d.getUTCMonth() + 1);
    }, [selectedDateEnd]);

    // Propaga início ao pai quando dia/mês/ano estão completos
    useEffect(() => {
        if (startDay && startMonth && schoolYear) {
            const y = Number(schoolYear);
            const m = Number(startMonth);
            const d = Number(startDay);
            setDayStart && setDayStart(d);
            setMonthStart && setMonthStart(m);
            setYearStart && setYearStart(y);
            setSelectedDateStart && setSelectedDateStart(new Date(y, m - 1, d));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDay, startMonth, schoolYear]);

    // Propaga término ao pai
    useEffect(() => {
        if (endDay && endMonth && schoolYear) {
            const y = Number(schoolYear);
            const m = Number(endMonth);
            const d = Number(endDay);
            setDayEnd && setDayEnd(d);
            setMonthEnd && setMonthEnd(m);
            setYearEnd && setYearEnd(y);
            setSelectedDateEnd && setSelectedDateEnd(new Date(y, m - 1, d));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endDay, endMonth, schoolYear]);

    const startDaysList = Array.from(
        { length: daysInMonth(schoolYear, startMonth) },
        (_, i) => i + 1
    );
    const endDaysList = Array.from(
        { length: daysInMonth(schoolYear, endMonth) },
        (_, i) => i + 1
    );

    const onStartMonthChange = (val) => {
        setStartMonthLocal(val);
        // Se o dia selecionado não existe no novo mês, ajusta
        if (startDay && Number(startDay) > daysInMonth(schoolYear, val)) {
            setStartDayLocal('');
        }
    };
    const onEndMonthChange = (val) => {
        setEndMonthLocal(val);
        if (endDay && Number(endDay) > daysInMonth(schoolYear, val)) {
            setEndDayLocal('');
        }
    };

    return (
        <Wrapper>
            <Group>
                <GroupLabel>Data de início</GroupLabel>
                <Row>
                    <Select
                        aria-label="Dia de início"
                        value={startDay}
                        onChange={(e) => setStartDayLocal(e.target.value)}
                    >
                        <option value="">Dia</option>
                        {startDaysList.map(d => (
                            <option key={d} value={d}>{String(d).padStart(2, '0')}</option>
                        ))}
                    </Select>
                    <Select
                        aria-label="Mês de início"
                        value={startMonth}
                        onChange={(e) => onStartMonthChange(e.target.value)}
                    >
                        <option value="">Mês</option>
                        {MONTHS.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </Select>
                    <YearBadge title="Ano letivo da escola">{schoolYear || '—'}</YearBadge>
                </Row>
            </Group>

            <Group>
                <GroupLabel>Data de término</GroupLabel>
                <Row>
                    <Select
                        aria-label="Dia de término"
                        value={endDay}
                        onChange={(e) => setEndDayLocal(e.target.value)}
                    >
                        <option value="">Dia</option>
                        {endDaysList.map(d => (
                            <option key={d} value={d}>{String(d).padStart(2, '0')}</option>
                        ))}
                    </Select>
                    <Select
                        aria-label="Mês de término"
                        value={endMonth}
                        onChange={(e) => onEndMonthChange(e.target.value)}
                    >
                        <option value="">Mês</option>
                        {MONTHS.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </Select>
                    <YearBadge title="Ano letivo da escola">{schoolYear || '—'}</YearBadge>
                </Row>
            </Group>
        </Wrapper>
    );
};

export default ResponsivePickers;

// ---- styles ----
const Wrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    width: 100%;
    margin-bottom: 8px;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const Group = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const GroupLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #475569;
`;

const Row = styled.div`
    display: flex;
    align-items: stretch;
    gap: 8px;
`;

const Select = styled.select`
    flex: 1;
    min-width: 0;
    box-sizing: border-box;
    padding: 11px 8px;
    border-radius: 10px;
    border: 1px solid #d7dfea;
    font-size: 15px;
    color: #1a2b45;
    background: var(--surface);
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
    }
`;

const YearBadge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid #dbe7fb;
    background: #f1f6ff;
    color: var(--blue, #3b82f6);
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
`;